// Insurance tab rebuild — TanStack Query wrappers over insuranceClaimsApi.
// Lives in its own module (usePortfolioAdmin.ts is frozen for concurrent
// work); query key extends the existing per-loan prefix so a loan-level
// invalidation still catches it.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  insuranceClaimsApi,
  type AdvanceInsuranceClaimRequest,
  type FileInsuranceClaimRequest,
  type InsuranceClaimDto,
} from "@/api/portfolioAdmin/insuranceClaimsApi";
import { invalidateLoanHealth } from "./invalidation";

export const insuranceClaimsKey = (loanId: string) =>
  ["portfolio-admin", "loan", loanId, "insurance-claims"] as const;

export function useInsuranceClaims(loanId: string | undefined) {
  return useQuery<InsuranceClaimDto[]>({
    queryKey: insuranceClaimsKey(loanId ?? ""),
    queryFn: () => insuranceClaimsApi.list(loanId!),
    enabled: !!loanId,
  });
}

export function useFileInsuranceClaim(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FileInsuranceClaimRequest) =>
      insuranceClaimsApi.file(loanId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: insuranceClaimsKey(loanId) });
      invalidateLoanHealth(qc, loanId);
    },
  });
}

export function useAdvanceInsuranceClaim(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { claimId: string; body: AdvanceInsuranceClaimRequest }) =>
      insuranceClaimsApi.advance(args.claimId, args.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: insuranceClaimsKey(loanId) });
      invalidateLoanHealth(qc, loanId);
    },
  });
}
